import styled from '@emotion/styled';
import React, {useRef, useState} from 'react';
import {ESearchInput} from 'webapp-lib/Components';
import {
  mixinFlexboxColumn,
  mixinOverflowYWithGutter,
  mixinTruncate,
} from 'webapp-lib/styling';
import {keyboardShortcutSearchHandler, useKeyboard} from 'webapp-lib/utils';

import {
  DraggableWorkLogSuggestion,
  EventApiWithWorkLogProps,
  EventTimers,
  WorkLogSuggestion,
} from '../types';
import {getEventDuration} from '../utilities/date';

import {RecentTemplate, StyledFavoriteCard} from './favorites/FavoriteTemplate';
import {Description} from './favorites/Favorites';
import {SectionTitle} from './favorites/styles';

export const CALENDAR_RECENT_CONTAINER = 'cal-recent-list';

const convertToWorkLogSuggestions = (events: EventApiWithWorkLogProps[]) =>
  events.map<DraggableWorkLogSuggestion>((e) => ({
    id: e.id,
    title: e.title,
    color: e.backgroundColor,
    description: e.extendedProps.description,
    defaultDurationSeconds: getEventDuration(e, 'seconds'),
    activity: e.extendedProps.activity,
  }));

const filterOutDuplicates = (
  workLogSuggestions: DraggableWorkLogSuggestion[]
) => {
  const unique = new Set<string>();
  return workLogSuggestions.filter((item) => {
    const uniqueKey = JSON.stringify({
      title: item.title,
      description: item.description,
      activityName: item.activity?.displayName,
    });

    if (!unique.has(uniqueKey)) {
      unique.add(uniqueKey);
      return true;
    }
    return false;
  });
};

const initSearchData = (
  events: EventApiWithWorkLogProps[]
): DraggableWorkLogSuggestion[] => {
  const allWorkLogSuggestions = convertToWorkLogSuggestions(events);
  return filterOutDuplicates(allWorkLogSuggestions);
};

export const RecentSearch = ({
  events,
  timer,
  handleMakeFavorite,
}: {
  events: EventApiWithWorkLogProps[];
  timer: EventTimers;
  handleMakeFavorite: (recent: DraggableWorkLogSuggestion) => void;
}): React.ReactElement => {
  const searchData = initSearchData(events);

  return (
    <StyledRecent>
      <SectionTitle>Einträge</SectionTitle>
      <RecentSearchContent
        handleMakeFavorite={handleMakeFavorite}
        searchData={searchData}
        timer={timer}
      />
    </StyledRecent>
  );
};

const RecentSearchContent = ({
  searchData,
  timer,
  handleMakeFavorite,
}: {
  searchData: WorkLogSuggestion[];
  timer: EventTimers;
  handleMakeFavorite: (recent: DraggableWorkLogSuggestion) => void;
}): React.ReactElement => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const searchQueryLowercase = searchQuery.toLowerCase();

  const matchesQuery = (text?: string) =>
    text?.toLowerCase().includes(searchQueryLowercase);

  const filteredSearchResults = searchData.filter(
    ({title, description, activity}) =>
      matchesQuery(title) ||
      matchesQuery(description) ||
      matchesQuery(activity?.displayName)
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    keyboardShortcutSearchHandler(e, () => {
      searchRef.current?.focus();
    });
  };
  useKeyboard(handleKeyDown);

  return (
    <>
      <ESearchInput
        autoFocus={false}
        inputRef={searchRef}
        placeholder='Search'
        value={searchQuery}
        onChange={setSearchQuery}
      />
      <StyledRecentList id={CALENDAR_RECENT_CONTAINER}>
        {filteredSearchResults.map((recent) => (
          <StyledRecentCard key={recent.id}>
            <RecentTemplate
              data={recent}
              onMakeFavorite={() => handleMakeFavorite(recent)}
              onStartTimer={() => timer.start(recent)}
            >
              <Title>{recent.title}</Title>
              <Description>{recent.description}</Description>
            </RecentTemplate>
          </StyledRecentCard>
        ))}
      </StyledRecentList>
    </>
  );
};

const StyledRecent = styled.div`
  ${mixinFlexboxColumn};

  padding-top: var(--gutter-medium);
  height: 30%;
`;

const StyledRecentList = styled.div`
  margin-top: var(--gutter-little);
  ${mixinOverflowYWithGutter};

  flex-grow: 1;
`;

const Title = styled.div`
  ${mixinTruncate};
  font-size: var(--font-size-small);
`;

const StyledRecentCard = styled.div`
  align-items: center;

  border: var(--color-box-border) solid 1px;
  cursor: move;
  margin: var(--gutter-tiny) 0;

  & ${StyledFavoriteCard} {
    padding: var(--gutter-tiny);
  }
`;
